export class StateSeqGate<TPayload = unknown> {
  private lastAppliedSeq = -1;
  private queue: { seq: number; payload: TPayload }[] = [];

  reset(): void {
    this.lastAppliedSeq = -1;
    this.queue = [];
  }

  getLastAppliedSeq(): number {
    return this.lastAppliedSeq;
  }

  hasQueued(): boolean {
    return this.queue.length > 0;
  }

  /**
   * Retourne true si l’état doit être mis en file (`isBusy`),
   * false s’il peut être appliqué immédiatement.
   *
   * Si l’état est obsolète (seq <= lastApplied), retourne null (à ignorer).
   *
   * Ne consulte PAS la file : un `drain` qui appelle `apply` alors que d’autres
   * états restent en file doit pouvoir appliquer sans re-enqueue (sinon deadlock).
   * Le caller socket doit passer `isBusy || hasQueued()`.
   */
  classifyIncoming(seq: number, isBusy: boolean): true | false | null {
    if (!Number.isFinite(seq)) return null;
    if (seq <= this.lastAppliedSeq) return null;
    return isBusy ? true : false;
  }

  enqueue(seq: number, payload: TPayload): void {
    if (this.queue.some((q) => q.seq === seq)) return;
    this.queue.push({ seq, payload });
    if (this.queue.length > 200) this.queue.shift();
  }

  markApplied(seq: number): void {
    if (!Number.isFinite(seq)) return;
    this.lastAppliedSeq = Math.max(this.lastAppliedSeq, seq);
  }

  /**
   * Applique au plus le prochain état de la file.
   * Les suivants restent en file : si `apply` démarre une animation (busy),
   * le prochain drain n’a lieu qu’après `markApplied` — évite d’enchaîner
   * N applications synchrones (re-render piste / UI) qui plantent l’onglet.
   */
  drain(apply: (payload: TPayload) => void): void {
    if (this.queue.length === 0) return;
    this.queue.sort((a, b) => a.seq - b.seq);
    this.queue = this.queue.filter((item) => item.seq > this.lastAppliedSeq);
    const next = this.queue.shift();
    if (!next) return;
    apply(next.payload);
  }
}
